import React, { FC } from 'react';
import { WarningAltOutlined } from '@duxton-web/icons';
import { Select, SelectProps, Tooltip } from '@duxton-web/ui';

import { SecondaryText, Text } from '../components';

import './CommonSelect.css';

const { Option } = Select;

export const filterOption = (search: string, node) => {
  const searchLowerCase = search.toLowerCase();
  const children =
    node?.children?.props?.children || node?.props?.children || [];

  const valueFound = (Array.isArray(children) ? children : [children]).some(
    (child) => {
      let label: string;

      if (child?.props?.['data-filter-ignore']) return false;

      if (typeof child === 'string') label = child;
      else if (typeof child?.props?.children === 'string')
        label = child.props.children;
      else {
        return filterOption(search, child);
      }

      return label && label.toLowerCase().includes(searchLowerCase);
    },
  );

  return valueFound || node?.key?.toLowerCase().includes(searchLowerCase);
};

export interface CommonSelectWrapperProps {
  id?: string;
  'data-testid'?: string;
  value?: string | string[];
  multiple?: boolean;
  onChange?: (value: string | string[] | any) => void;
  placeholder?: string;
  idKey?: string;
  metaMap?: { [key: string]: any };
  useMeta?: boolean;
  disabledMap?: { [key: string]: boolean };
  disabled?: boolean;
  style?: any;
  className?: string;
  allowClear?: boolean;
  defaultValue?: string;
  labelKey?: string;
  descriptionKey?: string;
  dropdownRender?: (menu: React.ReactElement) => React.ReactElement;
  onSearch?: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  selectRef?: React.RefObject<any>;
  popupMatchSelectWidth?: boolean;
}

export interface CommonSelectProps extends CommonSelectWrapperProps {
  values: any[];
  valueFn?: (item: any) => React.ReactNode;
  valueKey?: string;
  tagRender?: SelectProps['tagRender'];
}

const CommonSelect: FC<CommonSelectProps> = ({
  id,
  values,
  value,
  onChange,
  placeholder,
  idKey = 'id',
  valueKey = idKey,
  valueFn = (item) => item[valueKey],
  disabledMap = {},
  metaMap = {},
  useMeta = false,
  disabled = false,
  multiple,
  className = '',
  style,
  allowClear,
  defaultValue = '',
  labelKey,
  descriptionKey,
  dropdownRender,
  onSearch,
  onKeyDown,
  popupMatchSelectWidth = true,
  selectRef,
  tagRender,
  ...others
}: CommonSelectProps) => {
  const renderOptionLabel = (item) => {
    if (labelKey && descriptionKey)
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Text ellipsis>{item[labelKey]}</Text>
          <SecondaryText ellipsis>{item[descriptionKey]}</SecondaryText>
        </div>
      );

    if (labelKey && !descriptionKey)
      return <Text ellipsis>{item[labelKey]}</Text>;

    return valueFn(item);
  };

  return (
    <Select
      id={id}
      data-testid={others['data-testid']}
      className={className ? `${className} common-select` : `common-select`}
      style={
        style
          ? {
              ...style,
              ...{ height: labelKey && descriptionKey ? '52px' : null },
            }
          : {
              width: '100%',
              height: labelKey && descriptionKey ? '52px' : null,
            }
      }
      mode={multiple ? 'multiple' : undefined}
      value={value || defaultValue || undefined}
      allowClear={allowClear}
      showSearch
      dropdownRender={dropdownRender}
      onSearch={onSearch}
      onKeyDown={onKeyDown}
      ref={selectRef}
      {...{
        onChange,
        filterOption,
        placeholder,
        disabled,
      }}
      popupMatchSelectWidth={popupMatchSelectWidth}
      tagRender={tagRender}
    >
      {values?.map((item): any => (
        <Option
          key={item[idKey]}
          value={item[idKey]}
          disabled={disabledMap[item[idKey]]}
        >
          <div className="common-select-label-item">
            {metaMap[item[idKey]] && useMeta && (
              <span>
                <Tooltip title={metaMap[item[idKey]]}>
                  <WarningAltOutlined style={{ color: '#faad14' }} />
                </Tooltip>
              </span>
            )}
            {renderOptionLabel(item)}
          </div>
        </Option>
      ))}
    </Select>
  );
};

export { CommonSelect, CommonSelect as default };
